import React, { useEffect, useState } from 'react';
import { cn, normalizeKey } from '@/lib/utils';
import Link from 'next/link';
import { Separator } from "@/components/ui/separator";
import { builder } from '@builder.io/sdk';
import { Skeleton } from "@/components/ui/skeleton"
import Image from '@/components/ui/Image';

interface ProductData {
  product?: string;
  link?: string;
}

interface ProductDataResponse {
  data?: {
    products?: ProductData[];
  };
}

interface IndustryData {
  industry?: string;
  link?: string;
}

interface IndustryDataResponse {
  data?: {
    industries?: IndustryData[];
  };
}

interface CaseStudySummaryProps {
  summary: {
    hasCompanyLogo?: boolean;
    companyLogo?: string;
    companyLogoAltText?: string;
    companyProfile?: string;
    challenge?: string;
    showIndustry?: boolean;
    companySize?: string;
    showProducts?: boolean;
  }
  data?: {
    industry?: IndustryData[];
    products?: ProductData[];    
    location?: string;
  }
}

export const CaseStudySummary = ({ 
    summary,
    data,
}: CaseStudySummaryProps) => {
  const innerSectionClass = cn(
    'rounded-xl py-6 border gap-5 flex flex-col',
  );
  const blockClass = cn(
    'flex flex-col gap-1 px-4',
  );
  const titleClass = cn(
    'paragraph-sm text-foreground font-bold',
  );
  const contentClass = cn(
    'paragraph-sm text-foreground',
  );
  const listItemClass = cn(
    'flex flex-col gap-1 paragraph-sm text-foreground content-listitem',
  );

  const [isLoadingProducts, setIsLoadingProducts] = useState(true); 
  const [isLoadingIndustry, setIsLoadingIndustry] = useState(true);
  const [productData, setProductData] = useState<ProductDataResponse | null>(null);
  const [mappedProducts, setMappedProducts] = useState<ProductData[] | null>(null);
  const [unmatchedProducts, setUnmatchedProducts] = useState<ProductData[] | null>(null);
  const [industryData, setIndustryData] = useState<IndustryDataResponse | null>(null);
  const [mappedIndustry, setMappedIndustry] = useState<IndustryData[] | null>(null);
  const [unmatchedIndustry, setUnmatchedIndustry] = useState<IndustryData[] | null>(null);

  useEffect(() => {
    if (summary.showProducts && data?.products) {
      builder
        .get('products-data', {
          query: {
            name: "Product List"
          },
        })
        .then((res: ProductDataResponse) => {
          setProductData(res);  
        });
    }
    if (summary.showIndustry && data?.industry) {
      builder
        .get('industries-data', {
          query: {
            name: "Industry List"
          },
        })
        .then((res: IndustryDataResponse) => {
          setIndustryData(res);
        });
    }
  }, [summary.showProducts, summary.showIndustry, data?.products, data?.industry]);

  useEffect(() => {
    if (productData && productData.data && productData.data.products) {
      const productLinks = productData.data.products;

      const matchedProducts = data?.products
        ?.map((product) => {
          const matchedProduct = productLinks.find(
            (dataProduct) => dataProduct.product === product.product
          );
          return matchedProduct ?? null;
        })
        .filter((item): item is ProductData => item !== null);

      setMappedProducts(matchedProducts ?? null);

      const unmatchedProducts = data?.products?.filter((product) => {
        return !productLinks.some(
          (dataProduct) => dataProduct.product === product.product
        );
      });

      setUnmatchedProducts(unmatchedProducts ?? null);

      setIsLoadingProducts(false);
    }
  }, [productData, data?.products]);

  useEffect(() => {
    if (industryData && industryData.data && industryData.data.industries) {
      const industryLinks = industryData.data.industries;

      const matchedIndustry = data?.industry
        ?.map((industry) => {
          const matchedIndustry = industryLinks.find(
            (dataIndustry) => dataIndustry.industry === industry.industry
          );
          return matchedIndustry ?? null;
        })
        .filter((item): item is IndustryData => item !== null);

      setMappedIndustry(matchedIndustry ?? null);

      const unmatchedIndustry = data?.industry?.filter((industry) => {
        return !industryLinks.some(
          (dataIndustry) => dataIndustry.industry === industry.industry
        );
      }); 

      setUnmatchedIndustry(unmatchedIndustry ?? null);

      setIsLoadingIndustry(false);
    }
  }, [industryData, data?.industry]);

  return (
    <div className={innerSectionClass}>
      {summary.hasCompanyLogo && summary.companyLogo && (
        <div className={blockClass}>
          <div className="relative h-16 w-auto max-w-[160px]">
            <Image 
              src={summary.companyLogo} 
              alt={summary.companyLogoAltText ?? ''} 
              title={summary.companyLogoAltText ?? ''}
              fill
              className="object-contain object-left"
            />
          </div>
        </div>
      )}
      {summary.companyProfile && (
        <div className={blockClass}>
          <div className={titleClass}>
            Company Profile
          </div>
          <div className={contentClass}>
            {summary.companyProfile}
          </div>
        </div>
      )}
      {summary.challenge && (
        <div className={blockClass}>
          <div className={titleClass}>
            Challenge
          </div>
          <div className={contentClass}>
            {summary.challenge}
          </div>
        </div>
      )}
      {summary.showProducts && data?.products && (
        <div className={blockClass}>
          <div className={titleClass}>
            Products
          </div>
          <div className={listItemClass}>
            {isLoadingProducts ? (
              data?.products?.map((item) => ( 
                <Skeleton key={normalizeKey(item.product ?? '')} className="h-5 w-full bg-gray-200" />
              ))
            ) : (
              <p className='flex flex-col gap-1'>
                {mappedProducts?.map((item) => (
                  <Link key={normalizeKey(item.product ?? '')} href={item.link ?? ''} className='underlined'>
                    {item.product}
                  </Link>
                ))}
                {unmatchedProducts?.map((item) => (
                  <span key={normalizeKey(item.product ?? '')}>
                    {item.product}
                  </span>
                ))}
              </p>
            )}
          </div>  
        </div>
      )}
      {(data?.location || summary.companySize || summary.showIndustry) && (
        <Separator orientation="horizontal" className="block" />
      )}
      {data?.location && (
        <div className={blockClass}>
          <div className={titleClass}>
            Location
          </div>
          <div className={contentClass}>
            {data?.location}
          </div>
        </div>
      )}
      {summary.companySize && (
        <div className={blockClass}>
          <div className={titleClass}>
            Company Size
          </div>
          <div className={contentClass}>
            {summary.companySize}
          </div>
        </div>
      )}
      {summary.showIndustry && data?.industry && (
        <div className={blockClass}>
          <div className={titleClass}>
            Industry
          </div>  
          <div className={listItemClass}>
            {isLoadingIndustry ? (
              data?.industry?.map((item) => (
                <Skeleton key={normalizeKey(item.industry ?? '')} className="h-5 w-full bg-gray-200" />
              ))
            ) : (
              <p className='flex flex-col gap-1'>
                {mappedIndustry?.map((item) => (
                  <Link key={normalizeKey(item.industry ?? '')} href={item.link ?? ''} className='underlined'>
                    {item.industry}
                  </Link>
                ))}
                {unmatchedIndustry?.map((item) => (
                  <span key={normalizeKey(item.industry ?? '')}>
                    {item.industry}
                  </span>
                ))}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}; 
